import './AreaIndicatorEditPage.css';

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GenFrameworksContext, LoginContext } from '../../../App';
import Banner from '../../b2b-common/Banner';
import B2BStepper from '../../b2b-common/B2BStepper';
import SelectIndicator from '../components/area-indicators/SelectIndicator';
import useAccessControl from '../../b2b-users/hooks/useAccessControl';
import { fetchAllFrameworks } from '../../b2b-frameworks/pages/FrameworkSuitteExplorer';
import AreaIndicatorEditForm from '../components/area-indicators/AreaIndicatorEditForm';
import { saveNewAreaIndicator, updateAreaIndicator } from '../../../services/b2b-plans-apiService';
import { B2BContentContext } from '../../layout/Layout';
import { showNotification } from '../../b2b-common/Notification';
import { fetchOrganizationSensorsByOrgUUID, getAreaIndicatorSamplingConfiguration, saveNewSamplingConfiguration, updateSamplingConfiguration } from '../../../services/b2b-iotsampling-apiService';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';

export const SelectIndicatorContext = createContext();

const getPermissionsToCheck = (frameworkProduct) => {
    return [
        {
            productsRequired: [frameworkProduct],
            rolesRequired: ['ADMIN', 'EDITOR']
        },
        {
            rolesRequired: ['SUPER_ADMIN', 'SUPER_EDITOR']
        }
    ];
};

const handleAutomaticSamplingConfiguration = async (samplingConfiguration, areaIndicatorSaved, loginData, area, returnTo, plan, setNotification, t, navigate, setError) => {
    if (samplingConfiguration.areaIndicatorId === null) {
        const newSamplingConfiguration = {
            ...samplingConfiguration,
            areaIndicatorId: areaIndicatorSaved.areaIndicatorId,
        };
        console.log('newSamplingConfiguration:', newSamplingConfiguration);
        try {
            const automaticSamplingConfigSaved = await saveNewSamplingConfiguration(loginData.accessToken, loginData.loggedOrganizationUuid, newSamplingConfiguration);
            showNotification(setNotification, t('Area indicator and automatic sampling configuration saved'), 'success', 3000);
            navigate(returnTo, { state: { plan } });
        } catch (error) {
            setError(error.message);
            console.error('Error saving automatic sampling configuration:', error);
            showNotification(setNotification, t('Error saving automatic sampling configuration: ') + error.message, 'error', 5000);
        }
    } else if (samplingConfiguration.areaIndicatorId === areaIndicatorSaved.areaIndicatorId) {
        // Update the automatic sampling configuration
        try {
            const updatedSamplingConfiguration = await updateSamplingConfiguration(loginData.accessToken, loginData.loggedOrganizationUuid, areaIndicatorSaved.areaIndicatorId, samplingConfiguration);
            showNotification(setNotification, t('Area indicator and automatic sampling configuration updated'), 'success', 3000);
            navigate(returnTo, { state: { plan } });
        } catch (error) {
            setError(error.message);
            console.error('Error updating automatic sampling configuration:', error);
            showNotification(setNotification, t('Error updating automatic sampling configuration: ') + error.message, 'error', 5000);
        }
    }
};

const AreaIndicatorEditPage = () => {

    // CONTEXT VARIABLES

    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedIndicator, setSelectedIndicator] = useState(null);

    const [customIndicators, setCustomIndicators] = useState({
        data: [],
        isSearching: null,
        currentPage: 1,
        pagesCount: 1,
        refresh: true,
        pageNumberLoading: null
    });

    const { genericFrameworks } = useContext(GenFrameworksContext);
    const [frameworkIndicators, setFrameworkIndicators] = useState({
        iot: null,
        vertical: null,
        partner: null,
        software: null,
        all: genericFrameworks,
        data: [],
        isSearching: null,
        currentPage: 1,
        pagesCount: 1,
        pageNumberLoading: null,
        refresh: true,
        selectedFramework: null
    });

    // END CONTEXT VARIABLES

    const { t } = useB2BTranslation('b2b-plans');
    const navigate = useNavigate();
    const location = useLocation();

    const { plan } = location.state || {};
    const { area } = location.state || {};
    const { areaIndicator } = location.state || {};
    const returnTo = location.state?.from || '/plans/plans-explorer';

    const { setNotification } = useContext(B2BContentContext);
    const { loginData } = useContext(LoginContext);
    const [error, setError] = useState(null);

    const [initialAreaIndicator, setInitialAreaIndicator] = useState(areaIndicator ? {
        ...areaIndicator,
    } : {
        areaIndicatorName: t('New area indicator'),
        readOnly: false,
        areaIndicatorStatus: 'IDEA',
        samplingPeriodCronExpression: null,
        areaIndicatorWeight: 5,
    });

    const { checkAccess: checkIotAccess } = useAccessControl(getPermissionsToCheck('B2B_IOT_FW'));
    const { checkAccess: checkVerticalAccess } = useAccessControl(getPermissionsToCheck('B2B_VERTICALFW'));
    const { checkAccess: checkPartnerAccess } = useAccessControl(getPermissionsToCheck('B2B_PARTNERSFW'));
    const { checkAccess: checkSoftwareAccess } = useAccessControl(getPermissionsToCheck('B2B_SOFTWAREFW'));

    const [hasIotFrameworks, setHasIotFrameworks] = useState(checkIotAccess());
    const [hasVerticalFrameworks, setHasVerticalFrameworks] = useState(checkVerticalAccess());
    const [hasPartnerFrameworks, setHasPartnerFrameworks] = useState(checkPartnerAccess());
    const [hasSoftwareFrameworks, setHasSoftwareFrameworks] = useState(checkSoftwareAccess());

    const [sensorConfigs, setSensorConfigs] = useState(null);
    const [samplingConfiguration, setSamplingConfiguration] = useState(null);

    const initialFrameworksRef = useRef(frameworkIndicators.all);

    const getOrganizationSensors = async () => {
        // console.log('getOrganizationSensors');
        if (checkIotAccess) {
            try {
                const sensors = await fetchOrganizationSensorsByOrgUUID(loginData.accessToken, loginData.loggedOrganizationUuid);
                setSensorConfigs(sensors);
                if (sensors && sensors.length === 0) {
                    showNotification(setNotification, t('No sensors found for the organization.'), 'info', 5000);
                }
            } catch (error) {
                console.error('Error fetching organization sensors:', error);
                setError(error.message);
                showNotification(setNotification, t('Error fetching organization sensors: ') + error.message, 'error', 5000);
            }
        }
    };

    const retrieveAreaIndicatorSamplingConfiguration = async (areaIndicatorId) => {
        if (checkIotAccess) {
            try {
                const samplingConfig = await getAreaIndicatorSamplingConfiguration(loginData.accessToken, loginData.loggedOrganizationUuid, areaIndicatorId);
                // console.log('samplingConfig:', samplingConfig);
                setSamplingConfiguration(samplingConfig);
            } catch (error) {
                console.error('Error fetching area indicator sampling configuration:', error);
                setError(error.message);
                showNotification(setNotification, t('Error fetching area indicator sampling configuration: ') + error.message, 'error', 5000);
            }
        }
    };

    useEffect(() => {
        // Set areaIndicator as selectedIndicator if provided
        if (areaIndicator) {
            // console.log('AreaIndicatorEditPage areaIndicator:', areaIndicator);
            setSelectedIndicator(areaIndicator.indicatorDTO);
            if (areaIndicator.indicatorDTO.automaticSampling) {
                getOrganizationSensors();
                retrieveAreaIndicatorSamplingConfiguration(areaIndicator.areaIndicatorId);
            }
        }
    }, [areaIndicator]);

    // useEffect(() => {
    //     console.log('frameworkIndicators:', frameworkIndicators);
    // }, [frameworkIndicators]);

    useEffect(() => {
        // console.log('AreaIndicatorEditPage mounted.');
        // console.log('loginData:', loginData);
        const fetchFrameworks = async () => {
            const frameworks = [];

            const isFrameworkInAllFrameworks = (framework) => {
                return frameworkIndicators.all.some(existingFramework =>
                    existingFramework.frameworkMicroserviceAcronym === framework.frameworkMicroserviceAcronym &&
                    existingFramework.frameworkId === framework.frameworkId
                );
            };

            if (hasIotFrameworks && !frameworkIndicators.iot) {
                const iotFws = await fetchAllFrameworks(loginData, setError, 'iot');
                iotFws.forEach(fw => {
                    if (!isFrameworkInAllFrameworks(fw)) {
                        frameworks.push(fw);
                    }
                });
            }
            if (hasVerticalFrameworks && !frameworkIndicators.vertical) {
                const verticalFws = await fetchAllFrameworks(loginData, setError, 'vertical');
                verticalFws.forEach(fw => {
                    if (!isFrameworkInAllFrameworks(fw)) {
                        frameworks.push(fw);
                    }
                });
            }
            if (hasPartnerFrameworks && !frameworkIndicators.partner) {
                const partnerFws = await fetchAllFrameworks(loginData, setError, 'partner');
                partnerFws.forEach(fw => {
                    if (!isFrameworkInAllFrameworks(fw)) {
                        frameworks.push(fw);
                    }
                });
            }
            if (hasSoftwareFrameworks && !frameworkIndicators.software) {
                const softwareFws = await fetchAllFrameworks(loginData, setError, 'software');
                softwareFws.forEach(fw => {
                    if (!isFrameworkInAllFrameworks(fw)) {
                        frameworks.push(fw);
                    }
                });
            }
            setFrameworkIndicators(prevState => ({
                ...prevState,
                all: prevState.all.concat(frameworks)
            }));
        };
        if (initialFrameworksRef.current.length === genericFrameworks.length) {
            fetchFrameworks();
        }
    }, [loginData]);

    const handleSelectedIndicator = (indicator) => {
        setSelectedIndicator(indicator);
        // I will inherit the name of the indicator for the area indicator.
        setInitialAreaIndicator(prevState => ({
            ...prevState,
            areaIndicatorName: indicator.indicatorName,
            indicatorDTO: indicator,
            indicatorUUID: indicator.indicatorUUID,
        }));
        // if automaticSampling is true, I will retrieve the sensors of the organization.
        if (indicator.automaticSampling && !sensorConfigs) {
            getOrganizationSensors();
        }
    }

    const hasAutomaticSampling = selectedIndicator && selectedIndicator.automaticSampling;

    const handleSaveAreaIndicator = async () => {
        if (!areaIndicator) { // New area indicator
            try {
                const areaIndicatorSaved = await saveNewAreaIndicator(loginData.accessToken, plan.planUUID, area.areaId, initialAreaIndicator);
                // console.log('areaIndicatorSaved:', areaIndicatorSaved);
                const navigateTo = `${returnTo}?selectedAreaId=${area.areaId}&selectedAreaIndicatorId=${areaIndicatorSaved ? areaIndicatorSaved.areaIndicatorId : null}`;
                if (hasAutomaticSampling) {
                    handleAutomaticSamplingConfiguration(samplingConfiguration, areaIndicatorSaved,
                        loginData, area, navigateTo, plan, setNotification, t,
                        navigate, setError);
                } else {
                    showNotification(setNotification, t('Area indicator saved'), 'success', 3000);
                    navigate(navigateTo, { state: { plan } });
                }
            } catch (error) {
                setError(error.message);
                console.error('Error saving area indicator:', error);
                showNotification(setNotification, t('Error saving area indicator: ') + error.message, 'error', 3000);
            }
        } else { // Update area indicator
            try {
                const navigateTo = `${returnTo}?selectedAreaId=${area.areaId}&selectedAreaIndicatorId=${areaIndicator ? areaIndicator.areaIndicatorId : null}`;
                const updatedAreaIndicator = await updateAreaIndicator(loginData.accessToken, plan.planUUID, area.areaId, initialAreaIndicator);
                if (hasAutomaticSampling) {
                    handleAutomaticSamplingConfiguration(samplingConfiguration, updatedAreaIndicator,
                        loginData, area, navigateTo, plan, setNotification, t,
                        navigate, setError);
                } else {
                    showNotification(setNotification, t('Area indicator updated'), 'success', 3000);
                    navigate(navigateTo, { state: { plan } });
                }
            } catch (error) {
                setError(error.message);
                console.error('Error updating area indicator:', error);
                showNotification(setNotification, t('Error updating area indicator: ') + error.message, 'error', 3000);
            }
        }

    }

    const handleCancel = () => {
        const navigateTo = `${returnTo}?selectedAreaId=${area.areaId}&selectedAreaIndicatorId=${areaIndicator ? areaIndicator.areaIndicatorId : null}`;
        navigate(navigateTo, { state: { plan } });
    }

    const hasObjectives = areaIndicator && areaIndicator.objectiveDTOs && areaIndicator.objectiveDTOs.length > 0;
    const hasSamples = areaIndicator && areaIndicator.sampleDTOs && areaIndicator.sampleDTOs.length > 0;
    const hasObjectivesOrSamples = areaIndicator && (hasObjectives || hasSamples);

    const isAutomaticAndSensorSelected = samplingConfiguration &&
        samplingConfiguration.sourceConfigId > 0;

    const everythingIsReady = selectedIndicator && initialAreaIndicator.areaIndicatorName &&
        (!hasAutomaticSampling || (hasAutomaticSampling && isAutomaticAndSensorSelected));

    const steps_selecting_indicator = [
        {
            label: selectedIndicator ? `${t('Selected indicator')} [${selectedIndicator.indicatorName}].` : t('Select indicator.'),
            description: (
                <div className='area-indicator-edit-stepper-step-description'>
                    <p>{t('Select an indicator from the created custom indicators or from any available framework.')}</p>
                    <p>{t('Please note that if the area indicator has already objectives or samples, you cannot change the indicator.')}</p>
                </div>
            ),
            content: (<SelectIndicator onSelectedIndicator={handleSelectedIndicator} />),
            disableNext: (!selectedIndicator) ? true : false,
        },
        {
            label: t('Define remaining area indicator properties.'),
            description: (
                <div className='area-indicator-edit-stepper-step-description'>
                    <p>{t('Finish area indicator definition.')}</p>
                    {(areaIndicator && !hasObjectivesOrSamples) ?
                        (<p>{t('If you want to change the indicator, go back to the previous step.')}</p>) : null
                    }
                    {(areaIndicator && hasObjectivesOrSamples) ?
                        (<strong>{t('Since the area indicator has objectives or samples, you cannot change the indicator.')}</strong>) : null
                    }
                    {(hasAutomaticSampling) ?
                        (<p><strong>{t('Since the indicator has automatic sampling, you should select which sensor will provide samples.')}</strong></p>) : null
                    }
                    <p>{t('Finally set an optional custom name for the indicator, if there is an expected frequency for the sampling and the relevance of the area indicator by setting its weight.')}</p>
                    <p>{t('Click Finish when done.')}</p>
                </div>
            ),
            content: (<AreaIndicatorEditForm areaIndicator={initialAreaIndicator} setAreaIndicator={setInitialAreaIndicator} />),
            disableNext: everythingIsReady ? false : true,
        },
    ];

    const steps_not_selecting_indicator = [
        {
            label: t('Define area indicator properties.'),
            description: (
                <div className='area-indicator-edit-stepper-step-description'>
                    <h4>{t('Finish area indicator definition.')}</h4>
                    {(areaIndicator && hasObjectivesOrSamples) ?
                        (<p>{t('Please note that ')} <strong>{t('since the area indicator has objectives or samples ')}</strong>, {t('you cannot change the indicator.')}</p>) : null
                    }
                    {(hasAutomaticSampling && !isAutomaticAndSensorSelected) ?
                        (<p><strong>{t('Since the indicator has automatic sampling, and no sensor is selected, you still can select which sensor will provide samples.')}</strong></p>) : null
                    }
                    {(hasAutomaticSampling && isAutomaticAndSensorSelected && !hasSamples) ?
                        (<p><strong>{t('Since the indicator has automatic sampling, and there are no samples, you still can change which sensor will provide samples.')}</strong></p>) : null
                    }
                    <p>{t('Finally set an optional custom name for the indicator, if there is an expected frequency for the sampling and the relevance of the area indicator by setting its weight.')}</p>
                    <p>{t('Click Finish when done.')}</p>
                </div>
            ),
            content: (<AreaIndicatorEditForm areaIndicator={initialAreaIndicator} setAreaIndicator={setInitialAreaIndicator} />),
            disableNext: everythingIsReady ? false : true,
        },
    ];

    return (
        <SelectIndicatorContext.Provider value={{
            selectedTab, setSelectedTab,
            selectedIndicator, setSelectedIndicator,
            customIndicators, setCustomIndicators,
            frameworkIndicators, setFrameworkIndicators,
            sensorConfigs,
            samplingConfiguration, setSamplingConfiguration,
        }}>
            <div className='area-indicator-edit-page-container'>
                <Banner message={areaIndicator ? areaIndicator.areaIndicatorName : t('New area indicator.')} underline={false} />
                <B2BStepper
                    steps={hasObjectivesOrSamples ? steps_not_selecting_indicator : steps_selecting_indicator}
                    className={'area-indicator-edit-stepper'}
                    onSuccess={handleSaveAreaIndicator}
                    onCancel={handleCancel}
                    currentStep={hasObjectivesOrSamples ? 0 : !areaIndicator ? 0 : 1}
                />
            </div>
        </SelectIndicatorContext.Provider>
    );
};

export default AreaIndicatorEditPage;